import AvatarIcon from '@pelckmans/elementis/components/avatar/avatar-icon';
import Breadcrumbs from '@pelckmans/elementis/components/breadcrumbs';
import Button from '@pelckmans/elementis/components/button';
import Page from '@pelckmans/elementis/components/page';
import PillGroup from '@pelckmans/elementis/components/pill-group';
import LessonsIcon from '@pelckmans/elementis/icons/lessons';
import SidebarIcon from '@pelckmans/elementis/icons/sidebar';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import SublevelSummary from '../../../../components/module/SublevelSummary';
import { useRoutes } from '../../../../hooks/useRoutes';
import ContentHeader from '../../../../layout/content/ContentHeader';
import type { WithScrollThresholdProps } from '../../../../shared-components/elementis/hoc/withScrollThreshold';
import { ExerciseCategoriesContext } from '../../_contexts/ExerciseCategoriesContext';
import ModuleContext from '../../_contexts/ModuleContext';
import SidebarVisibilityContext from '../../_contexts/SidebarVisibilityContext';
import SuperModuleBreadcrumb from '../../components/breadcrumbs/SuperModuleBreadcrumb';
import VirtualExerciseModuleBreadcrumb from '../../components/breadcrumbs/VirtualExerciseModuleBreadcrumb';
import { useCategoryLinkBuilder } from '../hooks/useCategoryLinkBuilder';
import { findExerciseCategory } from '../util';

interface VirtualExerciseModuleHeaderProps extends WithScrollThresholdProps {}

function VirtualExerciseModuleHeaderBreadcrumbs() {
  const { t } = useTranslation();
  const { buildAbsolutePath } = useRoutes();
  const { subjectSlug, methodSlug, moduleSlug, selectedCategoryId } = useParams();
  const { buildCategoryLink } = useCategoryLinkBuilder();

  const { exerciseCategories } = useContext(ExerciseCategoriesContext);

  if (!subjectSlug || !methodSlug || !moduleSlug) return null;

  const selectedCategory = findExerciseCategory(exerciseCategories, selectedCategoryId || '');

  return (
    <>
      <SuperModuleBreadcrumb />
      <Breadcrumbs.Item
        avatarIcon={<AvatarIcon icon={<LessonsIcon />} />}
        to={buildAbsolutePath(['MATERIAL'], {
          subjectSlug,
          methodSlug,
          moduleSlug,
        })}
      >
        {t('BREADCRUMBS.SUPER_MODULE_LEVEL_PAGE')}
      </Breadcrumbs.Item>
      <VirtualExerciseModuleBreadcrumb />
      {selectedCategory && selectedCategory.length > 1 && (
        <Breadcrumbs.Item key={selectedCategory[0].id} to={buildCategoryLink(selectedCategory[0].id) || ''}>
          {selectedCategory[0].name}
        </Breadcrumbs.Item>
      )}
    </>
  );
}

function VirtualExerciseModuleHeader({ pastThreshold }: VirtualExerciseModuleHeaderProps) {
  const { t } = useTranslation();
  const { selectedCategoryId } = useParams();

  const { module } = useContext(ModuleContext);
  const { exerciseCategories } = useContext(ExerciseCategoriesContext);

  const exerciseCategoryWithHierarchy = findExerciseCategory(exerciseCategories, selectedCategoryId || '');

  const selectedCategory = exerciseCategoryWithHierarchy
    ? exerciseCategoryWithHierarchy[exerciseCategoryWithHierarchy?.length - 1]
    : null;

  const { totalExercises, totalCompletedExercises, totalScore } = exerciseCategories.reduce(
    (acc, exerciseCategory) => {
      acc.totalExercises += exerciseCategory.totalExercises || 0;
      acc.totalCompletedExercises += exerciseCategory.completedExercises || 0;

      acc.totalScore = {
        maximum: acc.totalScore.maximum + (exerciseCategory.score?.maximum || 0),
        achievable: acc.totalScore.achievable + (exerciseCategory.score?.achievable || 0),
        achieved: acc.totalScore.achieved + (exerciseCategory.score?.achieved || 0),
      };

      return acc;
    },
    {
      totalExercises: 0,
      totalCompletedExercises: 0,
      totalScore: { maximum: 0, achievable: 0, achieved: 0 },
    },
  );

  const totalContentTypeSummary = exerciseCategories
    .flatMap(x => (x.categories ? x.categories : x))
    .reduce<{ id: string; name: string; usage: number }[]>((acc, exerciseCategory) => {
      const contentTypeSummary = exerciseCategory.contentTypeSummary;

      if (!contentTypeSummary) return acc;

      for (const contentType of contentTypeSummary) {
        const existingContentType = acc.find(x => x.id === contentType.id);
        if (existingContentType) {
          existingContentType.usage += contentType.usage;
        } else {
          acc.push({ ...contentType });
        }
      }

      return acc;
    }, []);

  const { setSidebarVisibility } = useContext(SidebarVisibilityContext);

  if (!selectedCategory) {
    return (
      <ContentHeader
        cover={
          <Page.Header
            density={pastThreshold ? 'compact' : 'normal'}
            headerText={t('PAGES.VEM.HEADER.OVERVIEW.TITLE')}
            breadcrumbs={
              <Breadcrumbs aria-label={t('PAGES.TOC_LEVEL.SIDEBAR.BREADCRUMB.A11Y_LABEL')}>
                <VirtualExerciseModuleHeaderBreadcrumbs />
              </Breadcrumbs>
            }
            cover={{ backgroundImageUrl: module?.styling?.visual }}
            meta={
              <PillGroup role="group" aria-label={t('PAGES.VEM.HEADER.SUBLEVEL_SUMMARY_A11Y_LABEL')}>
                <SublevelSummary
                  sublevels={exerciseCategories}
                  totalExercises={totalExercises}
                  completedExercises={totalCompletedExercises}
                  contentTypeSummary={totalContentTypeSummary}
                  score={totalScore}
                />
              </PillGroup>
            }
            contextButton={
              <Button startIcon={<SidebarIcon />} onClick={() => setSidebarVisibility(true)}>
                {t('PAGES.VEM.HEADER.CONTEXT_BUTTON.OPEN_SIDEBAR')}
              </Button>
            }
          />
        }
      />
    );
  }

  return (
    <ContentHeader
      cover={
        <Page.Header
          density={pastThreshold ? 'compact' : 'normal'}
          headerText={selectedCategory.name}
          breadcrumbs={
            <Breadcrumbs aria-label={t('PAGES.TOC_LEVEL.SIDEBAR.BREADCRUMB.A11Y_LABEL')}>
              <VirtualExerciseModuleHeaderBreadcrumbs />
            </Breadcrumbs>
          }
          cover={{ backgroundImageUrl: module?.styling?.visual }}
          meta={
            <PillGroup role="group" aria-label={t('PAGES.VEM.HEADER.SUBLEVEL_SUMMARY_A11Y_LABEL')}>
              <SublevelSummary
                sublevels={selectedCategory.categories}
                totalExercises={selectedCategory.totalExercises}
                completedExercises={selectedCategory.completedExercises}
                contentTypeSummary={selectedCategory.contentTypeSummary}
                score={selectedCategory.score}
              />
            </PillGroup>
          }
          contextButton={
            <Button startIcon={<SidebarIcon />} onClick={() => setSidebarVisibility(true)}>
              {t('PAGES.VEM.HEADER.CONTEXT_BUTTON.OPEN_SIDEBAR')}
            </Button>
          }
        />
      }
    />
  );
}

export default VirtualExerciseModuleHeader;
